import ProgressBar from "../engine/components/ProgressBar";
import Pulse from "../engine/animations/Pulse";
import Transition from "../engine/animations/Transition";
import AnimationManager from "../engine/animations/AnimationManager";
import EventDispatcher from "../engine/EventDispatcher";
 
const CONTAINER_ID = "main-button";
const CONTENT_ID = "main-button-content";
const LOADING_LABEL_ID = "loading-text";
const LOGO_ID = "logo-img";
const PROGRESS_BAR_ID = "progress-bar";
const READY_LABEL_ID = "ready-text";

export class MainButton {
  private container: HTMLElement;
  private content: HTMLElement;
  private logo: HTMLElement;
  private progressBarElement: HTMLElement;

  private labels: {
    loading: HTMLElement;
    ready: HTMLElement;
  };

  private pulseAnimation: Animation;

  constructor() {
    this.initHTMLElements();
    EventDispatcher.registerEvent("main-button:clicked");
    ["click", "touchstart"].forEach((eventName) => {
      this.container.addEventListener(eventName, (event) => {
        event.preventDefault();
        event.stopPropagation();
        EventDispatcher.dispatch("main-button:clicked");
      });
    });
  }

  private initHTMLElements(): void {
    this.container = document.getElementById(CONTAINER_ID);
    this.content = document.getElementById(CONTENT_ID);
    this.logo = document.getElementById(LOGO_ID);
    this.progressBarElement = document.getElementById(PROGRESS_BAR_ID);

    this.labels = {
      loading: document.getElementById(LOADING_LABEL_ID),
      ready: document.getElementById(READY_LABEL_ID),
    };
  }

  public initProgressBar(length: number): ProgressBar {
    return new ProgressBar(this.progressBarElement, length);
  }

  public async showSignInLabel(): Promise<void> {
    const { container, content, labels } = this;
    const { loading, ready } = labels;

    await Promise.all([
      (await Transition.replace(loading, ready)).finished,
      Transition.blur(this.progressBarElement).finished,
    ]);

    this.pulseAnimation = Pulse.brightness(container);
    content.classList.add("clickable");
  }

  public async changeLogo(duration: number = 250, elementIn: HTMLElement, elementOut: HTMLElement): Promise<void> {
    const transitionConfig = {
      duration: duration / 2,
    };

    await Transition.fadeOut(elementIn, transitionConfig);
    await Transition.fadeScaleIn(elementOut, transitionConfig).finished;
    Pulse.scale(this.container);
  }

  public showLogo(duration: number = 250): void {
    this.changeLogo(duration, this.labels.ready, this.logo);
  }

  public async explode(duration: number = 250): Promise<void> {
    Transition.scaleOut(this.progressBarElement, { duration });
    this.progressBarElement.style.display = "none";
    await AnimationManager.stop(this.pulseAnimation);
  }

  public moveToBottom(): void {
    this.container.animate(
      [{ transform: "translate3D(-50%, 12.5vh, 0)" }],
      { duration: 1000, fill: "forwards", easing: "ease-in-out"  }
    )
  }

  public moveToCenter(): void {
    this.container.animate(
      [{ transform: "translate3D(-50%, 0, 0)" }],
      { duration: 1000, fill: "forwards", easing: "ease-in-out" }
    )
  }

  private async blurAction(action: Function) {
    await Transition.blur(this.logo, {
      duration: 500,
      easing: "ease-in",
    }).finished;
    action();
    Transition.unblur(this.logo, {
      duration: 500,
      easing: "ease-out",
    });

  } 

  public async showBackIcon(): Promise<void> {
    this.blurAction(() => {
      this.logo.classList.add("back-icon");
    });
  }

  public async removeBackIcon(): Promise<void> {
    this.blurAction(() => {
      this.logo.classList.remove("back-icon");
    });
  }
}
